import { Injectable, OnDestroy } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { Subject, merge, startWith, takeUntil } from 'rxjs';

/**
 * Material renders the paginator's labels ("Items per page", "1 - 10 of 57",
 * the arrow tooltips) from this service rather than from a template, so the
 * translate pipe can never reach them. Supplying a translated MatPaginatorIntl
 * is the only way to localise them.
 */
@Injectable()
export class TranslatedPaginatorIntl extends MatPaginatorIntl implements OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(private translate: TranslateService) {
    super();

    // onTranslationChange matters as much as onLangChange here: the JSON is
    // fetched over HTTP, so the first paginator can render before it lands.
    merge(
      this.translate.onLangChange,
      this.translate.onTranslationChange,
      this.translate.onDefaultLangChange
    )
      .pipe(startWith(null), takeUntil(this.destroy$))
      .subscribe(() => this.updateLabels());
  }

  private updateLabels(): void {
    this.itemsPerPageLabel = this.translate.instant('ItemsPerPage');
    this.nextPageLabel = this.translate.instant('NextPage');
    this.previousPageLabel = this.translate.instant('PreviousPage');
    this.firstPageLabel = this.translate.instant('FirstPage');
    this.lastPageLabel = this.translate.instant('LastPage');
    // Tells any live paginator to re-render with the new strings.
    this.changes.next();
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    const of = this.translate.instant('OfLabel');
    if (length === 0 || pageSize === 0) {
      return `0 ${of} ${length}`;
    }
    const total = Math.max(length, 0);
    const start = page * pageSize;
    // The last page is commonly shorter than the rest.
    const end = start < total ? Math.min(start + pageSize, total) : start + pageSize;
    return `${start + 1} - ${end} ${of} ${total}`;
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
